import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  CreateClientDto,
  clientsApi,
  emptyClientSigningDetails,
  handleApiError,
} from '../services/clientsService';
import { validateIdField, validateIsraeliId } from '../utils/validationUtils';
import ContactsEditor, { ContactEntry, convertToCreateContactDtos } from './ContactsEditor';
import { validatePhone } from '../services/leadsService';
import AddressConfirmationCheckbox from './AddressConfirmationCheckbox';
import { ADDRESS_CONFIRMATION_REQUIRED_ERROR } from '../constants/addressConfirmation';
import CollapsibleSection from './common/CollapsibleSection';
import AgentOrgAssignmentSection from './common/AgentOrgAssignmentSection';
import ClientAdditionalDetailsSection from './clientcase/ClientAdditionalDetailsSection';
import { departmentsApi, DepartmentDto } from '../services/settingsService';
import { useAuth } from '../contexts/AuthContext';

// BenefitType enum (matching backend)
enum BenefitType {
  GeneralPhysicalDisability = 1,
  GeneralMentalDisability = 2,
  DisabledChildPhysical = 3,
  DisabledChildMental = 4,
  NursingCare = 5,
  ShrM = 6,
  WorkDisability = 7,
  Mobility = 8,
  Other = 9
}

const BenefitTypeNames: Record<BenefitType, string> = {
  [BenefitType.GeneralPhysicalDisability]: 'נכות כללית מוחית',
  [BenefitType.GeneralMentalDisability]: 'נכות כללית נפשית',
  [BenefitType.DisabledChildPhysical]: 'ילד נכה - מוחית',
  [BenefitType.DisabledChildMental]: 'ילד נכה - נפשית',
  [BenefitType.NursingCare]: 'סיעוד',
  [BenefitType.ShrM]: 'שר"מ (שירותים רפואיים מיוחדים)',
  [BenefitType.WorkDisability]: 'נכות מעבודה',
  [BenefitType.Mobility]: 'ניידות',
  [BenefitType.Other]: 'אחר'
};

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background-color: var(--surface-color);
  border-radius: 8px;
  padding: 2rem;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--border-color);
`;

const ModalTitle = styled.h2`
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.5rem;

  &:hover {
    color: var(--text-primary);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const SectionTitle = styled.h3`
  color: var(--text-primary);
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  padding-top: 1rem;
  border-top: 1px solid var(--border-color);
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: var(--text-primary);
  font-weight: 500;
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  font-size: 1rem;
  direction: rtl;
  text-align: right;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(0, 142, 162, 0.1);
  }

  &.error {
    border-color: var(--error-color);
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  font-size: 1rem;
  direction: rtl;
  text-align: right;
  background-color: white;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(0, 142, 162, 0.1);
  }

  &.error {
    border-color: var(--error-color);
  }
`;

const Checkbox = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;

  input {
    cursor: pointer;
  }
`;

const InputWithCheckbox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SmallCheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8rem;
  color: var(--text-secondary);
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    color: var(--text-primary);
  }

  input {
    cursor: pointer;
    accent-color: var(--primary-color);
  }
`;

const ErrorMessage = styled.span`
  color: var(--error-color);
  font-size: 0.8rem;
  margin-top: 0.25rem;
`;

const SuccessMessage = styled.span`
  color: var(--success-color);
  font-size: 0.8rem;
  margin-top: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const AlertMessage = styled.div`
  background: var(--error-light);
  color: var(--error-color);
  padding: 12px 16px;
  border-radius: 4px;
  margin-bottom: 1rem;
  border: 1px solid var(--error-color);
  font-size: 0.9rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s ease;

  ${props => props.$variant === 'primary' ? `
    background-color: var(--primary-color);
    color: white;

    &:hover:not(:disabled) {
      background-color: var(--primary-hover);
    }
  ` : `
    background-color: transparent;
    color: var(--secondary-color);
    border: 1px solid var(--secondary-color);

    &:hover:not(:disabled) {
      background-color: var(--hover-background);
    }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

interface CreateClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onClientCreated: () => void;
}

const CreateClientModal: React.FC<CreateClientModalProps> = ({
  isOpen,
  onClose,
  onClientCreated
}) => {
  // אחוז עמלת ברירת מחדל
  const defaultCommission = 20;
  const { departmentIds } = useAuth();
  const [formData, setFormData] = useState<CreateClientDto>({
    fullName: '',
    idNumber: '',
    isForeignResident: false, // האם תושב חו"ל
    phone: '',
    email: '',
    departmentId: undefined,
    commissionPercentage: defaultCommission,
    benefitType: undefined,
    address: '',
    city: '',
    addressConfirmed: false, // אישור התאמת כתובת למשרד הפנים — חובה כאשר address/city מולאו
    birthDate: '',
    wasHospitalizedLastYear: false,
    hadSurgeryLastYear: false,
    submittedClaimBefore: false,
    nationalInsuranceUsername: '',
    nationalInsurancePassword: '',
    // פרטי חשבון בנק להעברת קצבה - פרטי מקבל הקצבה
    beneficiaryName: '',
    beneficiaryIdNumber: '',
    bankCode: '',
    branchNumber: '',
    accountNumber: '',
    // פרטי משלם הקצבה
    payerName: '',
    payerIdNumber: '',
    isPayerForeignResident: false,
    ...emptyClientSigningDetails(),
  });

  // אנשי קשר (טלפון + אימייל אופציונלי)
  const [phoneEntries, setPhoneEntries] = useState<ContactEntry[]>([
    { id: 0, number: '', email: '', label: 'ראשי', isPrimary: true, sortOrder: 0 }
  ]);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [generalError, setGeneralError] = useState<string>('');
  const [departments, setDepartments] = useState<DepartmentDto[]>([]);

  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens
      setFormData({
        fullName: '',
        idNumber: '',
        isForeignResident: false,
        phone: '',
        email: '',
        // ברירת-מחדל למחלקה = מחלקת המשתמש היוצר (אם משויך); ניתן לשינוי/לניקוי
        departmentId: departmentIds && departmentIds.length > 0 ? departmentIds[0] : undefined,
        commissionPercentage: defaultCommission,
        benefitType: undefined,
        address: '',
        city: '',
        addressConfirmed: false,
        wasHospitalizedLastYear: false,
        hadSurgeryLastYear: false,
        submittedClaimBefore: false,
        nationalInsuranceUsername: '',
        nationalInsurancePassword: '',
        // פרטי חשבון בנק להעברת קצבה - פרטי מקבל הקצבה
        beneficiaryName: '',
        beneficiaryIdNumber: '',
        bankCode: '',
        branchNumber: '',
        accountNumber: '',
        // פרטי משלם הקצבה
        payerName: '',
        payerIdNumber: '',
        isPayerForeignResident: false,
        ...emptyClientSigningDetails(),
      });
      setPhoneEntries([{ id: 0, number: '', email: '', label: 'ראשי', isPrimary: true, sortOrder: 0 }]);
      setErrors({});
      setGeneralError('');

      // Load departments
      loadDepartments();
    }
  }, [isOpen, defaultCommission, departmentIds]);

  const loadDepartments = async () => {
    try {
      const data = await departmentsApi.getAll();
      setDepartments(data);
    } catch (err) {
      console.error('Error loading departments:', err);
    }
  };

  const validateField = (field: string, value: any): string => {
    switch (field) {
      case 'fullName':
        if (!value || !value.trim()) {
          return 'שם מלא הוא שדה חובה';
        }
        break;

      case 'idNumber':
        if (!value || !value.trim()) {
          return 'תעודת זהות היא שדה חובה';
        } else {
          // Use validateIdField with foreign resident check
          const idError = validateIdField(value, formData.isForeignResident || false);
          if (idError) return idError;
        }
        break;

      case 'phone':
        if (!value || !value.trim()) {
          return 'טלפון הוא שדה חובה';
        } else if (value.trim().length < 9) {
          return 'מספר טלפון חייב להכיל לפחות 9 ספרות';
        }
        break;

      case 'email':
        if (value && value.trim() && !/\S+@\S+\.\S+/.test(value)) {
          return 'פורמט הדוא"ל אינו תקין';
        }
        break;

      case 'commissionPercentage':
        const numValue = Number(value);
        if (isNaN(numValue)) {
          return 'אחוז עמלה חייב להיות מספר';
        } else if (numValue < 0.01 || numValue > 100) {
          return 'אחוז עמלה חייב להיות בין 0.01 ל-100';
        }
        break;

      default:
        return '';
    }
    return '';
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    const fullNameError = validateField('fullName', formData.fullName);
    if (fullNameError) newErrors.fullName = fullNameError;

    const idNumberError = validateField('idNumber', formData.idNumber);
    if (idNumberError) newErrors.idNumber = idNumberError;

    // וידוא מספרי טלפון
    const primaryPhone = phoneEntries.find(p => p.isPrimary);
    if (!primaryPhone || !primaryPhone.number?.trim()) {
      newErrors.phone = 'טלפון ראשי הוא שדה חובה';
    } else if (!validatePhone(primaryPhone.number)) {
      newErrors.phone = 'מספר טלפון ראשי לא תקין';
    }

    const invalidPhones = phoneEntries.filter(p => p.number.trim() && !validatePhone(p.number));
    if (invalidPhones.length > 0) {
      newErrors.contacts = 'יש מספרי טלפון לא תקינים';
    }

    const emailError = validateField('email', formData.email);
    if (emailError) newErrors.email = emailError;

    const commissionError = validateField('commissionPercentage', formData.commissionPercentage);
    if (commissionError) newErrors.commissionPercentage = commissionError;

    // אישור התאמת כתובת — חובה רק אם מולאה כתובת או עיר
    const hasAddress = !!(formData.address && formData.address.trim());
    const hasCity = !!(formData.city && formData.city.trim());
    if ((hasAddress || hasCity) && !formData.addressConfirmed) {
      newErrors.addressConfirmed = ADDRESS_CONFIRMATION_REQUIRED_ERROR;
    }

    // ולידציה לפרטי חשבון בנק — אופציונלי, אך all-or-nothing
    const bankFields = [
      formData.beneficiaryName,
      formData.beneficiaryIdNumber,
      formData.bankCode,
      formData.branchNumber,
      formData.accountNumber,
    ].map(v => (v || '').trim());
    const anyBankFilled = bankFields.some(v => v !== '');
    const allBankFilled = bankFields.every(v => v !== '');
    if (anyBankFilled && !allBankFilled) {
      newErrors.bankGeneral = 'יש למלא את כל פרטי חשבון הבנק או להשאיר את כולם ריקים';
    }
    if (allBankFilled) {
      const digitsOnly = /^\d+$/;
      if (!digitsOnly.test((formData.bankCode || '').trim())) {
        newErrors.bankCode = 'ניתן להזין ספרות בלבד';
      }
      if (!digitsOnly.test((formData.branchNumber || '').trim())) {
        newErrors.branchNumber = 'ניתן להזין ספרות בלבד';
      }
      if (!digitsOnly.test((formData.accountNumber || '').trim())) {
        newErrors.accountNumber = 'ניתן להזין ספרות בלבד';
      }
      const beneficiaryIdError = validateIdField((formData.beneficiaryIdNumber || '').trim(), false);
      if (beneficiaryIdError) {
        newErrors.beneficiaryIdNumber = beneficiaryIdError;
      }
    }

    // ולידציה לת.ז. משלם הקצבה
    const payerIdError = validateIdField((formData.payerIdNumber || '').trim(), formData.isPayerForeignResident ?? false);
    if (payerIdError) {
      newErrors.payerIdNumber = payerIdError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setGeneralError('');

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // מציאת הטלפון הראשי
      const primaryPhone = phoneEntries.find(p => p.isPrimary);

      // Prepare data - remove empty optional fields
      const dataToSubmit: CreateClientDto = {
        fullName: formData.fullName.trim(),
        idNumber: formData.idNumber.trim(),
        isForeignResident: formData.isForeignResident,
        phone: primaryPhone?.number?.trim() || formData.phone.trim(),
        contacts: convertToCreateContactDtos(phoneEntries),
        commissionPercentage: formData.commissionPercentage,
        wasHospitalizedLastYear: formData.wasHospitalizedLastYear,
        hadSurgeryLastYear: formData.hadSurgeryLastYear,
        submittedClaimBefore: formData.submittedClaimBefore,
        // פרטי חתימה — נשלחים תמיד (overwrite מלא)
        beneficiaryType: formData.beneficiaryType,
        fatherName: formData.fatherName?.trim() || undefined,
        fatherIdNumber: formData.fatherIdNumber?.trim() || undefined,
        fatherAddress: formData.fatherAddress?.trim() || undefined,
        fatherCity: formData.fatherCity?.trim() || undefined,
        fatherBirthDate: formData.fatherBirthDate || undefined,
        isFatherForeignResident: formData.isFatherForeignResident,
        motherName: formData.motherName?.trim() || undefined,
        motherIdNumber: formData.motherIdNumber?.trim() || undefined,
        motherAddress: formData.motherAddress?.trim() || undefined,
        motherCity: formData.motherCity?.trim() || undefined,
        motherBirthDate: formData.motherBirthDate || undefined,
        isMotherForeignResident: formData.isMotherForeignResident,
        spouseName: formData.spouseName?.trim() || undefined,
        spouseAddress: formData.spouseAddress?.trim() || undefined,
        spouseCity: formData.spouseCity?.trim() || undefined,
        spouseIdNumber: formData.spouseIdNumber?.trim() || undefined,
        spouseBirthDate: formData.spouseBirthDate || undefined,
        isSpouseForeignResident: formData.isSpouseForeignResident,
        submitterSourceType: formData.submitterSourceType,
        submitterName: formData.submitterName?.trim() || undefined,
        submitterRelationship: formData.submitterRelationship?.trim() || undefined,
        submitterIdNumber: formData.submitterIdNumber?.trim() || undefined,
        submitterAddress: formData.submitterAddress?.trim() || undefined,
        submitterCity: formData.submitterCity?.trim() || undefined,
      };

      if (formData.email && formData.email.trim()) {
        dataToSubmit.email = formData.email.trim();
      }

      if (formData.benefitType !== undefined) {
        dataToSubmit.benefitType = formData.benefitType;
      }

      if (formData.departmentId !== undefined) {
        dataToSubmit.departmentId = formData.departmentId;
      }

      if (formData.address && formData.address.trim()) {
        dataToSubmit.address = formData.address.trim();
      }

      if (formData.city && formData.city.trim()) {
        dataToSubmit.city = formData.city.trim();
      }

      // נשלח רק כש-address/city מולאו — אחרת ה-flag לא רלוונטי בצד השרת
      if (dataToSubmit.address || dataToSubmit.city) {
        dataToSubmit.addressConfirmed = formData.addressConfirmed;
      }

      if (formData.birthDate) {
        dataToSubmit.birthDate = formData.birthDate;
      }

      if (formData.nationalInsuranceUsername && formData.nationalInsuranceUsername.trim()) {
        dataToSubmit.nationalInsuranceUsername = formData.nationalInsuranceUsername.trim();
      }

      if (formData.nationalInsurancePassword && formData.nationalInsurancePassword.trim()) {
        dataToSubmit.nationalInsurancePassword = formData.nationalInsurancePassword.trim();
      }

      // פרטי חשבון בנק להעברת קצבה — נשלחים רק אם מולאו (all-or-nothing נאכף ב-validateForm)
      if (formData.beneficiaryName && formData.beneficiaryName.trim()) {
        dataToSubmit.beneficiaryName = formData.beneficiaryName.trim();
        dataToSubmit.beneficiaryIdNumber = formData.beneficiaryIdNumber?.trim();
        dataToSubmit.bankCode = formData.bankCode?.trim();
        dataToSubmit.branchNumber = formData.branchNumber?.trim();
        dataToSubmit.accountNumber = formData.accountNumber?.trim();
      }

      // פרטי משלם הקצבה
      if (formData.payerName && formData.payerName.trim()) {
        dataToSubmit.payerName = formData.payerName.trim();
      }
      if (formData.payerIdNumber && formData.payerIdNumber.trim()) {
        dataToSubmit.payerIdNumber = formData.payerIdNumber.trim();
        dataToSubmit.isPayerForeignResident = formData.isPayerForeignResident;
      }

      await clientsApi.createClient(dataToSubmit);

      onClientCreated();
      onClose();
    } catch (err) {
      const errorMessage = handleApiError(err);
      if (errorMessage.includes('משלם הקצבה')) setErrors(prev => ({ ...prev, payerIdNumber: errorMessage }));
      else setGeneralError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string | number | boolean | undefined) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    if (generalError) {
      setGeneralError('');
    }
  };

  const handleBlur = (field: string, value: any) => {
    const error = validateField(field, value);
    if (error) {
      setErrors(prev => ({ ...prev, [field]: error }));
    } else {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const isFieldValid = (field: string, value: any): boolean => {
    return !validateField(field, value) && value && value.toString().trim() !== '';
  };

  if (!isOpen) return null;

  return (
    <Overlay>
      <Modal>
        <ModalHeader>
          <ModalTitle>יצירת לקוח חדש</ModalTitle>
          <CloseButton onClick={onClose} type="button">×</CloseButton>
        </ModalHeader>

        {generalError && (
          <AlertMessage>{generalError}</AlertMessage>
        )}

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>שם מלא *</Label>
            <Input
              type="text"
              value={formData.fullName}
              onChange={(e) => handleInputChange('fullName', e.target.value)}
              onBlur={(e) => handleBlur('fullName', e.target.value)}
              className={errors.fullName ? 'error' : ''}
              disabled={isLoading}
              placeholder="הזן שם מלא"
            />
            {errors.fullName && <ErrorMessage>{errors.fullName}</ErrorMessage>}
            {!errors.fullName && isFieldValid('fullName', formData.fullName) && (
              <SuccessMessage>✓ תקין</SuccessMessage>
            )}
          </FormGroup>

          <FormGroup>
            <Label>תעודת זהות *</Label>
            <InputWithCheckbox>
              <Input
                type="text"
                value={formData.idNumber}
                onChange={(e) => handleInputChange('idNumber', e.target.value)}
                onBlur={(e) => handleBlur('idNumber', e.target.value)}
                className={errors.idNumber ? 'error' : ''}
                disabled={isLoading}
                placeholder="123456789"
                maxLength={9}
                style={{ flex: 1 }}
              />
              <SmallCheckboxLabel>
                <input
                  type="checkbox"
                  checked={formData.isForeignResident || false}
                  onChange={(e) => {
                    handleInputChange('isForeignResident', e.target.checked);
                    // Clear ID error when toggling foreign resident
                    if (errors.idNumber) {
                      setErrors(prev => ({ ...prev, idNumber: '' }));
                    }
                  }}
                  disabled={isLoading}
                />
                תושב חו"ל
              </SmallCheckboxLabel>
            </InputWithCheckbox>
            {errors.idNumber && <ErrorMessage>{errors.idNumber}</ErrorMessage>}
            {!errors.idNumber && formData.idNumber && (formData.isForeignResident || validateIsraeliId(formData.idNumber)) && (
              <SuccessMessage>✓ {formData.isForeignResident ? 'תושב חו"ל' : 'תעודת זהות תקינה'}</SuccessMessage>
            )}
          </FormGroup>

          <FormGroup>
            <Label>אנשי קשר *</Label>
            <ContactsEditor
              contacts={phoneEntries}
              onChange={(newEntries) => {
                setPhoneEntries(newEntries);
                // ניקוי שגיאות
                if (errors.phone) {
                  setErrors(prev => ({ ...prev, phone: '' }));
                }
                if (errors.contacts) {
                  setErrors(prev => ({ ...prev, contacts: '' }));
                }
              }}
              disabled={isLoading}
            />
            {errors.phone && <ErrorMessage>{errors.phone}</ErrorMessage>}
            {errors.contacts && <ErrorMessage>{errors.contacts}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label>דוא"ל</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              onBlur={(e) => handleBlur('email', e.target.value)}
              className={errors.email ? 'error' : ''}
              disabled={isLoading}
              placeholder="example@mail.com"
            />
            {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
            {!errors.email && formData.email && isFieldValid('email', formData.email) && (
              <SuccessMessage>✓ כתובת דוא"ל תקינה</SuccessMessage>
            )}
          </FormGroup>

          <FormGroup>
            <Label>מחלקה</Label>
            <Select
              value={formData.departmentId || ''}
              onChange={(e) => handleInputChange('departmentId', e.target.value ? parseInt(e.target.value) : undefined)}
              disabled={isLoading}
            >
              <option value="">ללא מחלקה</option>
              {departments.filter(d => d.isActive || d.id === formData.departmentId).map(dep => (
                <option key={dep.id} value={dep.id}>
                  {dep.name}
                </option>
              ))}
            </Select>
          </FormGroup>

          {/* P8b: שיוך סוכן/ארגון מפנה + עמלות פר-תיק — ביצירה כל הצוות ממלא (showCommissions=true) */}
          <AgentOrgAssignmentSection
            value={{
              agentSourceId: formData.agentSourceId,
              organizationSourceId: formData.organizationSourceId,
              agentSigningCommissionAmount: formData.agentSigningCommissionAmount,
              agentApprovalCommissionPercentage: formData.agentApprovalCommissionPercentage,
              organizationSigningCommissionAmount: formData.organizationSigningCommissionAmount,
              organizationApprovalCommissionPercentage: formData.organizationApprovalCommissionPercentage,
            }}
            onChange={(patch) => setFormData(prev => ({ ...prev, ...patch }))}
            showCommissions={true}
            disabled={isLoading}
          />

          <FormGroup>
            <Label>כתובת</Label>
            <Input
              type="text"
              value={formData.address}
              onChange={(e) => handleInputChange('address', e.target.value)}
              disabled={isLoading}
              placeholder="רחוב ומספר בית"
              maxLength={200}
            />
          </FormGroup>

          <FormGroup>
            <Label>עיר</Label>
            <Input
              type="text"
              value={formData.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
              disabled={isLoading}
              placeholder="שם העיר"
              maxLength={100}
            />
          </FormGroup>

          {/* אישור התאמת כתובת — חובה רק כאשר מולאה כתובת או עיר */}
          <FormGroup>
            <AddressConfirmationCheckbox
              checked={!!formData.addressConfirmed}
              onChange={(checked) => {
                setFormData(prev => ({ ...prev, addressConfirmed: checked }));
                if (checked && errors.addressConfirmed) {
                  setErrors(prev => {
                    const next = { ...prev };
                    delete next.addressConfirmed;
                    return next;
                  });
                }
              }}
              disabled={isLoading}
              error={errors.addressConfirmed}
            />
          </FormGroup>

          <FormGroup>
            <Label>תאריך לידה</Label>
            <Input
              type="date"
              value={formData.birthDate || ''}
              onChange={(e) => handleInputChange('birthDate', e.target.value)}
              disabled={isLoading}
              max={new Date().toISOString().split('T')[0]}
            />
          </FormGroup>

          {/* ============================================================
              פרטים נוספים — מקטע מתכווץ עם סוג מוטב + הורים / בן זוג / מגיש
              ============================================================ */}
          <CollapsibleSection title="פרטים נוספים" defaultOpen={false}>
            <ClientAdditionalDetailsSection
              value={formData}
              clientBase={{
                address: formData.address,
                city: formData.city,
                idNumber: formData.idNumber,
                birthDate: formData.birthDate,
                isForeignResident: formData.isForeignResident,
                benefitType: formData.benefitType,
              }}
              errors={errors}
              mode="create"
              onChange={(patch) => setFormData(prev => ({ ...prev, ...patch }))}
              onErrorsChange={setErrors}
            />
          </CollapsibleSection>

          {/* פרטי חשבון בנק להעברת קצבה — מקטע מתכווץ, מקופל כברירת מחדל
              מיקום: אחרי "פרטים נוספים" */}
          <CollapsibleSection title="פרטי חשבון בנק להעברת קצבה" defaultOpen={false}>
            {errors.bankGeneral && (
              <AlertMessage style={{ marginBottom: 12 }}>{errors.bankGeneral}</AlertMessage>
            )}

            <SectionTitle>פרטי מקבל הקצבה</SectionTitle>

            <FormGroup>
              <Label>שם מוטב</Label>
              <Input
                type="text"
                value={formData.beneficiaryName || ''}
                onChange={(e) => {
                  handleInputChange('beneficiaryName', e.target.value);
                  if (errors.bankGeneral) {
                    setErrors(prev => { const n = { ...prev }; delete n.bankGeneral; return n; });
                  }
                }}
                disabled={isLoading}
                placeholder="שם בעל החשבון"
                maxLength={100}
              />
              {errors.beneficiaryName && <ErrorMessage>{errors.beneficiaryName}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <Label>ת.ז. מוטב</Label>
              <Input
                type="text"
                value={formData.beneficiaryIdNumber || ''}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '').slice(0, 9);
                  handleInputChange('beneficiaryIdNumber', v);
                  if (errors.bankGeneral) {
                    setErrors(prev => { const n = { ...prev }; delete n.bankGeneral; return n; });
                  }
                }}
                disabled={isLoading}
                placeholder="123456789"
                maxLength={9}
                inputMode="numeric"
                className={errors.beneficiaryIdNumber ? 'error' : ''}
              />
              {errors.beneficiaryIdNumber && <ErrorMessage>{errors.beneficiaryIdNumber}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <Label>קוד בנק</Label>
              <Input
                type="text"
                value={formData.bankCode || ''}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '');
                  handleInputChange('bankCode', v);
                  if (errors.bankGeneral) {
                    setErrors(prev => { const n = { ...prev }; delete n.bankGeneral; return n; });
                  }
                }}
                disabled={isLoading}
                placeholder="לדוגמה: 12"
                maxLength={10}
                inputMode="numeric"
                className={errors.bankCode ? 'error' : ''}
              />
              {errors.bankCode && <ErrorMessage>{errors.bankCode}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <Label>מספר סניף</Label>
              <Input
                type="text"
                value={formData.branchNumber || ''}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '');
                  handleInputChange('branchNumber', v);
                  if (errors.bankGeneral) {
                    setErrors(prev => { const n = { ...prev }; delete n.bankGeneral; return n; });
                  }
                }}
                disabled={isLoading}
                placeholder="לדוגמה: 123"
                maxLength={10}
                inputMode="numeric"
                className={errors.branchNumber ? 'error' : ''}
              />
              {errors.branchNumber && <ErrorMessage>{errors.branchNumber}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <Label>מספר חשבון</Label>
              <Input
                type="text"
                value={formData.accountNumber || ''}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '');
                  handleInputChange('accountNumber', v);
                  if (errors.bankGeneral) {
                    setErrors(prev => { const n = { ...prev }; delete n.bankGeneral; return n; });
                  }
                }}
                disabled={isLoading}
                placeholder="מספר חשבון"
                maxLength={30}
                inputMode="numeric"
                className={errors.accountNumber ? 'error' : ''}
              />
              {errors.accountNumber && <ErrorMessage>{errors.accountNumber}</ErrorMessage>}
            </FormGroup>

            <SectionTitle>פרטי משלם הקצבה</SectionTitle>

            <FormGroup>
              <Label>שם משלם הקצבה</Label>
              <Input
                type="text"
                value={formData.payerName || ''}
                onChange={(e) => handleInputChange('payerName', e.target.value)}
                disabled={isLoading}
                placeholder="שם משלם הקצבה"
                maxLength={100}
              />
            </FormGroup>

            <FormGroup>
              <Label>ת.ז. משלם הקצבה</Label>
              <InputWithCheckbox>
                <Input
                  type="text"
                  value={formData.payerIdNumber || ''}
                  onChange={(e) => {
                    const v = formData.isPayerForeignResident
                      ? e.target.value
                      : e.target.value.replace(/\D/g, '').slice(0, 9);
                    handleInputChange('payerIdNumber', v);
                    if (errors.payerIdNumber) {
                      setErrors(prev => ({ ...prev, payerIdNumber: '' }));
                    }
                  }}
                  onBlur={(e) => {
                    const err = validateIdField(e.target.value, formData.isPayerForeignResident ?? false);
                    setErrors(prev => ({ ...prev, payerIdNumber: err || '' }));
                  }}
                  disabled={isLoading}
                  placeholder="123456789"
                  maxLength={formData.isPayerForeignResident ? 20 : 9}
                  inputMode="numeric"
                  className={errors.payerIdNumber ? 'error' : ''}
                  style={{ flex: 1 }}
                />
                <SmallCheckboxLabel>
                  <input
                    type="checkbox"
                    checked={formData.isPayerForeignResident || false}
                    onChange={(e) => {
                      handleInputChange('isPayerForeignResident', e.target.checked);
                      if (errors.payerIdNumber) {
                        setErrors(prev => ({ ...prev, payerIdNumber: '' }));
                      }
                    }}
                    disabled={isLoading}
                  />
                  תושב חו"ל
                </SmallCheckboxLabel>
              </InputWithCheckbox>
              {errors.payerIdNumber && <ErrorMessage>{errors.payerIdNumber}</ErrorMessage>}
            </FormGroup>
          </CollapsibleSection>

          <SectionTitle>פרטי עמלה וזכאות</SectionTitle>

          <FormGroup>
            <Label>אחוז עמלה (%) *</Label>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              max="100"
              value={formData.commissionPercentage}
              onChange={(e) => handleInputChange('commissionPercentage', parseFloat(e.target.value) || 20)}
              onBlur={(e) => handleBlur('commissionPercentage', e.target.value)}
              className={errors.commissionPercentage ? 'error' : ''}
              disabled={isLoading}
              placeholder="20"
            />
            {errors.commissionPercentage && <ErrorMessage>{errors.commissionPercentage}</ErrorMessage>}
            {!errors.commissionPercentage && isFieldValid('commissionPercentage', formData.commissionPercentage) && (
              <SuccessMessage>✓ אחוז עמלה תקין</SuccessMessage>
            )}
          </FormGroup>

          <FormGroup>
            <Label>סוג קצבה</Label>
            <Select
              value={formData.benefitType || ''}
              onChange={(e) => handleInputChange('benefitType', e.target.value ? parseInt(e.target.value) : undefined)}
              disabled={isLoading}
            >
              <option value="">בחר סוג קצבה...</option>
              {Object.entries(BenefitTypeNames).map(([key, name]) => (
                <option key={key} value={key}>
                  {name}
                </option>
              ))}
            </Select>
          </FormGroup>

          <SectionTitle>שאלות רפואיות</SectionTitle>

          <FormGroup>
            <Checkbox>
              <input
                type="checkbox"
                checked={formData.wasHospitalizedLastYear}
                onChange={(e) => handleInputChange('wasHospitalizedLastYear', e.target.checked)}
                disabled={isLoading}
              />
              אושפז בשנה האחרונה
            </Checkbox>
          </FormGroup>

          <FormGroup>
            <Checkbox>
              <input
                type="checkbox"
                checked={formData.hadSurgeryLastYear}
                onChange={(e) => handleInputChange('hadSurgeryLastYear', e.target.checked)}
                disabled={isLoading}
              />
              עבר ניתוח בשנה האחרונה
            </Checkbox>
          </FormGroup>

          <FormGroup>
            <Checkbox>
              <input
                type="checkbox"
                checked={formData.submittedClaimBefore}
                onChange={(e) => handleInputChange('submittedClaimBefore', e.target.checked)}
                disabled={isLoading}
              />
              הגיש תביעה בעבר
            </Checkbox>
          </FormGroup>

          <SectionTitle>פרטי ביטוח לאומי (אופציונלי)</SectionTitle>

          <FormGroup>
            <Label>שם משתמש לאתר ביטוח לאומי</Label>
            <Input
              type="text"
              value={formData.nationalInsuranceUsername}
              onChange={(e) => handleInputChange('nationalInsuranceUsername', e.target.value)}
              disabled={isLoading}
              placeholder="שם משתמש"
            />
          </FormGroup>

          <FormGroup>
            <Label>סיסמה לאתר ביטוח לאומי</Label>
            <Input
              type="password"
              value={formData.nationalInsurancePassword}
              onChange={(e) => handleInputChange('nationalInsurancePassword', e.target.value)}
              disabled={isLoading}
              placeholder="סיסמה"
            />
          </FormGroup>

          <ButtonGroup>
            <Button type="button" onClick={onClose} disabled={isLoading}>
              ביטול
            </Button>
            <Button type="submit" $variant="primary" disabled={isLoading}>
              {isLoading ? 'יוצר לקוח...' : 'צור לקוח'}
            </Button>
          </ButtonGroup>
        </Form>
      </Modal>
    </Overlay>
  );
};

export default CreateClientModal;
